/**
 * Score Grid Block Component
 * Displays multiple performance metrics in a grid layout with progress bars
 */

import { ScoreGridBlock as ScoreGridBlockType } from "@/types/interview-feedback.types";
import { HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ScoreGridBlockProps {
  block: ScoreGridBlockType;
  compact?: boolean;
  showAverageIndicator?: boolean;
}

export const ScoreGridBlock = ({ block, compact = false, showAverageIndicator = false }: ScoreGridBlockProps) => {
  const { helper, items, title } = block;

  return (
    <div className="space-y-6">
      {/* Block Title */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-foreground">{title}</h2>
          {showAverageIndicator && (
            <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded">
              Average
            </span>
          )}
        </div>
        {helper && (
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <HelpCircle className="w-4 h-4" />
                  <span>What does this mean?</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="max-w-xs">
                <p className="text-sm whitespace-normal">{helper}</p>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Score Grid */}
      <div className="space-y-4">
          {items.map((item, index) => {
            // Backend always sends scores in the correct scale (0-maxScore)
            // Just clamp to valid range to be safe
            const displayScore = Math.min(Math.max(item.score, 0), item.maxScore);
            const percentage = Math.round((displayScore / item.maxScore) * 100);
            const barColor = item.bar_color || "#3b82f6";
            const bgColor = item.bar_background_color || "#1e293b";

            return (
              <div
                key={index}
                className="space-y-3 w-full p-4 rounded-lg bg-muted/20 backdrop-blur-sm transition-all hover:bg-muted/30"
              >
                {/* Score Header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-1.5">
                          <h3 className={compact ? "text-base font-semibold text-foreground" : "text-lg font-semibold text-foreground"}>
                            {item.title}
                          </h3>
                      {item.helper && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="inline-flex items-center justify-center flex-shrink-0">
                              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="max-w-xs">
                            <p className="text-sm whitespace-normal">{item.helper}</p>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div
                      className={compact ? "text-xl font-bold tabular-nums" : "text-2xl font-bold tabular-nums"}
                      style={{ color: barColor }}
                    >
                      {displayScore.toFixed(1)}
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      / {item.maxScore}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1 w-full">
                  <div
                    className="h-2 rounded-full overflow-hidden w-full"
                    style={{ backgroundColor: bgColor }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: barColor,
                        boxShadow: `0 0 8px ${barColor}40`,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
